import React, { useContext, useEffect, useState } from "react";
import { Button, Spin, Row, Col, message } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import {
  generateDischargeSummaryApi,
  getDischargeSummaryApi,
  submitDischargeSummaryApi,
} from "../../../../services/discharge.services";
import { getCookie } from "../../../../helpers/localStorage";
import { Role } from "../../../../constants/defaultKeys";
import useQuery from "../../../../hooks/useQuery";
import { UserContext } from "../../../../context/UserContext";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import dischargeImage from "../../../../assets/images/nurseIcons/dischargeSummary.svg";
import ShareModal from "./ShareModal";
import "./DischargeSummaryMobile.css";

export default function DischargeSummary({ patientData }) {
  const { userDetails } = useContext(UserContext);
  const [value, setValue] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const params = useQuery();
  const isActive = params?.status === "current";
  const role = getCookie(Role) || userDetails?.currRole;
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    getDischargeSummaryApi(params?.patient, role)
      .then((res) => {
        setSummaryData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [refresh]);

  const handleEditorChange = (content) => {
    setValue(content);
  };

  const handleSubmit = async () => {
    try {
      const response = await submitDischargeSummaryApi(params?.patient, role, {
        html: value,
      });
      message.success(response?.data?.message);
      setRefresh(true);
      console.log("Content submitted successfully:", response.data);
    } catch (error) {
      console.error("Error submitting content:", error);
      message.error(error?.response?.data?.message);
    }
  };

  const generateSummary = async () => {
    try {
      setLoading(true);

      const res = await generateDischargeSummaryApi(params?.patient, role);

      setSummaryData(res?.data);
    } catch (err) {
      console.error(err);

      message.error(
        err?.response?.data?.message ||
          "Unable to generate discharge summary. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="discharge-summary-view">
      {loading ? (
        <Row>
          <Col span={24} style={{ textAlign: "center" }}>
            <Spin tip="Loading Discharge Summary..." />
          </Col>
        </Row>
      ) : summaryData ? (
        <>
          <div
            className="flex-end discharge-summary-actions"
            style={{ gap: "24px", marginBottom: "10px" }}
          >
            <Button type="primary" onClick={() => setOpenModal(true)}>
              Share Discharge Summary
            </Button>
          </div>
          <Editor
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
            className="discharge-summary-editor"
            init={{
              height: 650,
              mobile: {
                menubar: false,
              },
              plugins: [
                "anchor",
                "autolink",
                "charmap",
                "codesample",
                "emoticons",
                "image",
                "link",
                "lists",
                "media",
                "searchreplace",
                "table",
                "visualblocks",
                "wordcount",
              ],

              content_style: `
              .container {
                max-width: 800px;
                margin: 40px auto;
                padding: 20px;
                background-color: white;
              }
              h1 {
                text-align: center;
                color: #7244d5;
                font-weight: 600;
              }
              h2,
              h3 {
                color: #7244d5;
                margin-top: 20px;
                text-align: left;
              }
              .info-section {
                 display: flex;
                 justify-content: space-between;
                 margin-bottom: 10px;
                 padding-bottom: 20px;
                 border-bottom: 1px solid #e6e6e6;
              }
              .info-section label{
                font-weight: bold;
                color: #333;
              }
              .section {
                margin-bottom: 20px;
                border-bottom: 1px solid #e6e6e6;
              }
              
              .section label {
                font-weight: bold;
                color: #333;
              }
              .section div {
                margin-bottom: 10px;
                font-size: 14px;
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              table,
              th,
              td {
                border: 1px solid #ddd;
              }
              th,
              td {
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f1f1f1;
              }
              td {
                color: #555;
              }
              .footer {
                text-align: center;
                margin-top: 40px;
                font-size: 14px;
                color: #777;
              }
              .footer a {
                color: #7244d5;
                text-decoration: none;
                font-weight: 600;
              }
              .footer a:hover {
                text-decoration: underline;
              }
              .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid #e6e6e6;
                padding-bottom: 20px;
              }
              
              .address {
                width: 200px;
                font-size: small;
              }
              .follow-up-advice {
                border-bottom: none;
              }
              
              .custom-list li {
                 margin-bottom: 10px;
              }
              @media (max-width: 768px) {
                .container {
                  max-width: 100%;
                  margin: 0;
                  padding: 12px;
                }
                h1 {
                  font-size: 20px;
                }
                h2,
                h3 {
                  font-size: 16px;
                  margin-top: 16px;
                }
                .info-section,
                .header {
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 12px;
                }
                .info-section > div,
                .header > div {
                  width: 100%;
                  min-width: 0;
                }
                .info-section > div > div,
                .section > div {
                  margin-bottom: 10px;
                  line-height: 1.5;
                  word-break: break-word;
                }
                .address {
                  width: 100%;
                }
                table {
                  display: block;
                  overflow-x: auto;
                  -webkit-overflow-scrolling: touch;
                }
                th,
                td {
                  white-space: normal;
                  word-break: break-word;
                }
              }`,

              toolbar:
                "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat",
            }}
            initialValue={summaryData}
            onEditorChange={handleEditorChange}
          />
          <div style={{ marginTop: "10px", textAlign: "right" }}>
            <Button onClick={handleSubmit} type="primary" size="large">
              Submit
            </Button>
          </div>
          {openModal && (
            <ShareModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              id={params?.patient}
              practitioner={role}
              patientData={patientData}
            />
          )}
        </>
      ) : (
        <>
          <Row>
            <Col span={24} style={{ textAlign: "center" }}>
              <NoDataPage
                title={"Discharge Summary Not generated"}
                subtitle={"Please click below to generate discharge summary"}
                image={dischargeImage}
              />
              <Button onClick={generateSummary} type="primary" size="large">
                Generate{" "}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
// plugins: [
//   "anchor",
//   "autolink",
//   "charmap",
//   "codesample",
//   "emoticons",
//   "image",
//   "link",
//   "lists",
//   "media",
//   "searchreplace",
//   "table",
//   "visualblocks",
//   "wordcount",
//   "checklist",
//   "mediaembed",
//   "casechange",
//   "export",
//   "formatpainter",
//   "pageembed",
//   "a11ychecker",
//   "tinymcespellchecker",
//   "permanentpen",
//   "powerpaste",
//   "advtable",
//   "advcode",
//   "editimage",
//   "advtemplate",
//   "mentions",
//   "tinycomments",
//   "tableofcontents",
//   "footnotes",
//   "mergetags",
//   "autocorrect",
//   "typography",
//   "inlinecss",
//   "markdown",
//   "importword",
//   "exportword",
//   "exportpdf",
// ],
